import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useState } from 'react';

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((val, key) => body.append(key, val.toString()));
    await fetch('https://readdy.ai/api/form/d7dfbm6m3fsfopf23rjg', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-rose-500 via-rose-400 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 to-zinc-950/95"></div>
        <div className="relative z-10 px-8 md:px-16 lg:px-24">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-px bg-rose-500"></div>
            <span className="text-rose-400 text-xs font-semibold uppercase tracking-[0.25em]">Get In Touch</span>
          </div>
          <h1
            className="text-5xl md:text-7xl font-black text-white leading-tight mb-6"
            style={{ fontFamily: "'Syne', sans-serif" }}
          >
            Contact<br /><span className="text-rose-500">Us</span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-xl leading-relaxed">
            We're here to answer your questions and help you get the care you need.
          </p>
        </div>
      </section>

      {/* Info cards */}
      <section className="bg-zinc-50 pt-16 pb-0">
        <div className="px-8 md:px-16 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
            {[
              { icon: 'ri-map-pin-line', title: 'Our Location', lines: ['4820 W Flamingo Rd, Suite 100', 'Las Vegas, NV 89103'] },
              { icon: 'ri-phone-line', title: 'Phone', lines: ['(702) 555-0192', 'Mon–Fri 8am–6pm, Sat 9am–2pm'] },
              { icon: 'ri-mail-line', title: 'Email', lines: ['info@axispointmedical.com', 'We respond within 1 business day'] },
            ].map((c) => (
              <div key={c.title} className="bg-white rounded-2xl p-7 border border-zinc-100 flex gap-4 items-start">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-rose-50 text-rose-500 shrink-0">
                  <i className={`${c.icon} text-lg`}></i>
                </div>
                <div>
                  <h3
                    className="font-black text-zinc-900 mb-1 text-sm"
                    style={{ fontFamily: "'Syne', sans-serif" }}
                  >
                    {c.title}
                  </h3>
                  {c.lines.map((l) => <p key={l} className="text-zinc-500 text-sm">{l}</p>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Map + Form */}
      <section className="bg-zinc-50 pb-20">
        <div className="px-8 md:px-16 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Map */}
            <div className="rounded-2xl overflow-hidden border border-zinc-100 h-[480px]">
              <iframe
                title="AxisPoint Medical Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3223.5!2d-115.2172!3d36.1147!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzbCsDA2JzUyLjkiTiAxMTXCsDEzJzAyLjAiVw!5e0!3m2!1sen!2sus!4v1680000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
              ></iframe>
            </div>

            {/* Form */}
            <div className="bg-white rounded-2xl p-8 border border-zinc-100">
              {submitted ? (
                <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-rose-50 text-rose-500 mb-5">
                    <i className="ri-check-line text-3xl"></i>
                  </div>
                  <h3
                    className="text-2xl font-black text-zinc-900 mb-3"
                    style={{ fontFamily: "'Syne', sans-serif" }}
                  >
                    Message Sent!
                  </h3>
                  <p className="text-zinc-500 text-sm max-w-xs">Thank you for reaching out. We'll get back to you within 1 business day.</p>
                </div>
              ) : (
                <>
                  <h2
                    className="text-2xl font-black text-zinc-900 mb-7"
                    style={{ fontFamily: "'Syne', sans-serif" }}
                  >
                    Send Us a Message
                  </h2>
                  <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-zinc-500 mb-1.5 uppercase tracking-wider">First Name *</label>
                        <input name="first_name" required className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-rose-400 transition-colors" placeholder="Jane" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-zinc-500 mb-1.5 uppercase tracking-wider">Last Name *</label>
                        <input name="last_name" required className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-rose-400 transition-colors" placeholder="Smith" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-zinc-500 mb-1.5 uppercase tracking-wider">Email *</label>
                      <input name="email" type="email" required className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-rose-400 transition-colors" placeholder="jane@email.com" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-zinc-500 mb-1.5 uppercase tracking-wider">Phone</label>
                      <input name="phone" className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-rose-400 transition-colors" placeholder="(702) 555-0000" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-zinc-500 mb-1.5 uppercase tracking-wider">Subject *</label>
                      <select name="subject" required className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-rose-400 bg-white cursor-pointer transition-colors">
                        <option value="">Select a topic</option>
                        <option>General Inquiry</option>
                        <option>Appointment Question</option>
                        <option>Insurance & Billing</option>
                        <option>Medical Records</option>
                        <option>Prescription Refill</option>
                        <option>Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-zinc-500 mb-1.5 uppercase tracking-wider">Message *</label>
                      <textarea
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full border border-zinc-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-rose-400 resize-none transition-colors"
                        placeholder="How can we help you?"
                        onChange={(e) => setCharCount(e.target.value.length)}
                      />
                      <div className="text-right text-xs text-zinc-400 mt-1">{charCount}/500</div>
                    </div>
                    <button
                      type="submit"
                      className="w-full py-4 bg-rose-500 hover:bg-rose-400 text-white font-bold rounded-full transition-colors cursor-pointer whitespace-nowrap text-sm"
                    >
                      Send Message
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
